import type { ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AppRouter } from '@/router';
import { VerifyEmailPage } from '@/pages/VerifyEmailPage';
import { NativeGoogleSignInPage } from '@/pages/NativeGoogleSignInPage';
import { NativeAppleSignInPage } from '@/pages/NativeAppleSignInPage';
import { NativeFacebookSignInPage } from '@/pages/NativeFacebookSignInPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/lib/auth/react';
import { AuthModal } from '@/components/features/Auth/AuthModal';
import { EmailVerificationPage } from '@/components/features/Auth/EmailVerificationPage';
import { OtpModal } from '@/components/features/Otp';
import { TermsGate } from '@/components/features/Documents/TermsGate';
import { KycGate } from '@/components/features/Kyc/KycGate';
import { KycActionGate } from '@/components/features/Kyc/KycActionGate';
import { TerritoryBlockGate } from '@/components/features/Territory/TerritoryBlockGate';
import { KillSwitchGate } from '@/components/features/KillSwitch/KillSwitchGate';
import { DepositLimitGate } from '@/components/features/DepositLimit/DepositLimitGate';
import { AccountStatusGate } from '@/components/features/AccountStatus/AccountStatusGate';
import { IncomingNotificationBanner } from '@/components/features/Notifications/IncomingNotificationBanner';
import { NotificationDetail } from '@/components/features/Notifications/NotificationDetail';
import { useNotificationsRealtime } from '@/lib/notifications/useNotificationsRealtime';
import { AnalyticsProvider, createAnalyticsMutationCache } from '@/analytics';
import { LaunchDarklyProvider } from '@/lib/launchdarkly/LaunchDarklyProvider';
import { ExperimentsDevtools } from '@/lib/experiments';
import { FtueGate } from '@/components/features/Ftue/FtueGate';
import { installOtpInterceptor } from '@/lib/install-otp-interceptor';
import { installAuthRefreshInterceptor } from '@/lib/auth/install-auth-refresh-interceptor';
import { installConsentInterceptor } from '@/lib/documents/install-consent-interceptor';
import { installSignupInterceptor } from '@/lib/auth/install-signup-interceptor';
import { installKycInterceptor } from '@/lib/kyc/install-kyc-interceptor';
import { installTerritoryInterceptor } from '@/lib/territory/install-territory-interceptor';
import { installKillSwitchInterceptor } from '@/lib/killSwitch/install-kill-switch-interceptor';
import { installDepositLimitInterceptor } from '@/lib/depositLimit/install-deposit-limit-interceptor';
import { installAccountStatusInterceptor } from '@/lib/accountStatus/install-account-status-interceptor';
import { AXIOS_INSTANCE } from '@/lib/use-custom-instance';
import { retryUnlessHardHttp } from '@/lib/query-retry';
import { useRealtimeResync } from '@/lib/useRealtimeResync';
import { useSocketAuthSync } from '@/lib/useSocketAuthSync';
import { useKycRealtime } from '@/lib/kyc/useKycRealtime';
import { useKycActionAutoGate } from '@/lib/kyc/useKycActionAutoGate';
import { RealtimeManagerProvider } from '@/lib/realtime';
import { RealtimePrivateBridge } from '@/lib/realtime/RealtimePrivateBridge';
import { IntercomMessenger } from '@/lib/intercom';
import { useWalletBalanceRealtime } from '@/lib/wallet/useWalletBalanceRealtime';
import { authClient } from '@/auth';
import { IS_E2E } from '@/test-support/e2e-mode';
import { MockAuthProvider } from '@/test-support/MockAuthProvider';

/**
 * Root application component.
 * Delegates routing to {@link AppRouter}; providers live here.
 */

// Wire mutation lifecycle to analytics. The QueryClient is shared by every
// Orval-generated hook, so this single cache captures every mutation success
// and failure without per-feature wiring. See trackMutation.ts.
const queryClient = new QueryClient({
  mutationCache: createAnalyticsMutationCache(),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      // Don't hammer a hard HTTP error (4xx/5xx) with retries — try once.
      retry: retryUnlessHardHttp,
    },
  },
});

// Install the OTP response interceptor on the shared axios instance. Every
// Orval-generated hook funnels through `AXIOS_INSTANCE` via
// `useCustomInstance`, so wiring this once covers every mutation and query.
installOtpInterceptor(AXIOS_INSTANCE);

// Reactive session refresh: any 401 (expired signed JWT) mints a fresh access
// token from the live Firebase session and replays the request once — the
// backstop for the AuthClient's proactive refresh timer.
installAuthRefreshInterceptor(AXIOS_INSTANCE);

// Backstop for the server-side ConsentGuard: a 403 CONSENT_REQUIRED nudges the
// TermsGate to refetch pending docs and surface the blocking modal.
installConsentInterceptor(AXIOS_INSTANCE);

// Backstop for the server-side signup-completion gate (AuthenticationGuard):
// a 403 SIGNUP_INCOMPLETE on any mutating request reopens the signup funnel at
// the user's next step and replays the request once signup completes.
installSignupInterceptor(AXIOS_INSTANCE);

// Backstop for the server-side KycGuard: a 403 KYC_REQUIRED opens the KYC
// verification popup for the first pending flow.
installKycInterceptor(AXIOS_INSTANCE);

// Backstop for the server-side TerritoryGuard / category enforcer: a 403
// TERRITORY_BLOCKED surfaces the territory block screen.
installTerritoryInterceptor(AXIOS_INSTANCE);

// Backstop for the server-side kill switches: a 503 MAINTENANCE / FEATURE_DISABLED
// surfaces the kill-switch gate (reactive complement to the client-side LD UX).
installKillSwitchInterceptor(AXIOS_INSTANCE);

// CP-007b: a 403 DEPOSIT_LIMIT_EXCEEDED from createIntent surfaces the deposit-limit
// popup. Its own interceptor (matched by `code`) so parsePaymentApiError can't
// flatten the breach into a generic permission error.
installDepositLimitInterceptor(AXIOS_INSTANCE);

// CP-007b: a 403 ACCOUNT_CLOSED (suspended) / ACCOUNT_LOCKED (banned) surfaces the
// account-blocked screen. Enforced server-side by the AuthenticationGuard.
installAccountStatusInterceptor(AXIOS_INSTANCE);

function AuthShell({ children }: { children: ReactNode }): ReactNode {
  if (IS_E2E) return <MockAuthProvider>{children}</MockAuthProvider>;
  return <AuthProvider client={authClient}>{children}</AuthProvider>;
}

/**
 * Inside QueryClientProvider so useQueryClient() resolves. Stays separate
 * from App() so it can use hooks without forcing App() to be a child of
 * QueryClientProvider.
 */
function RealtimeBridge(): null {
  // First: re-handshake the shared socket when the session identity changes
  // (guest → signed-in after signup/login, signed-in → guest on logout), so
  // the hooks below actually receive user-scoped events.
  useSocketAuthSync();
  useRealtimeResync();
  useKycRealtime();
  useNotificationsRealtime();
  // The identity KycGate is reactive only: it opens via the 403 KYC_REQUIRED
  // interceptor when the user reaches a guarded action. We deliberately do not
  // probe status on init and pop it for a non-APPROVED (e.g. PENDING) flow.
  useKycActionAutoGate();
  return null;
}

/**
 * Realtime hooks that depend on the PrivateEventBus. Separate from
 * {@link RealtimeBridge} because the bus provider lives deeper in the tree
 * (inside {@link RealtimePrivateBridge}); this must render under it.
 */
function PrivateRealtimeBridge(): null {
  useWalletBalanceRealtime();
  return null;
}

function App() {
  // `/verify-email` and `/reset-password` are standalone PUBLIC landings reached from an
  // email link. They render outside the authenticated provider tree on purpose: no
  // AuthProvider (no session needed), and outside the TermsGate/KycGate/etc. scope so a gate
  // can never intercept them. They only need a Router for their query params, so they get a
  // minimal BrowserRouter of their own.
  if (window.location.pathname === '/verify-email') {
    return (
      <BrowserRouter>
        <VerifyEmailPage />
      </BrowserRouter>
    );
  }

  // `/auth/native-google-signin` is opened by the RN app in the **system browser** (not the
  // WebView) to work around Google blocking OAuth inside WebViews. It runs the full auth
  // flow (signInWithPopup → /auth/login → deep link) and needs no authenticated session,
  // so it renders outside AuthShell like VerifyEmailPage.
  if (window.location.pathname === '/auth/native-google-signin') {
    return (
      <BrowserRouter>
        <NativeGoogleSignInPage />
      </BrowserRouter>
    );
  }

  // Same pattern as Google: Apple OAuth via `signInWithPopup` fails inside WebViews,
  // so the RN app opens this page in the system browser.
  if (window.location.pathname === '/auth/native-apple-signin') {
    return (
      <BrowserRouter>
        <NativeAppleSignInPage />
      </BrowserRouter>
    );
  }

  // Same pattern as Google/Apple: Facebook OAuth via `signInWithPopup` fails inside
  // WebViews, so the RN app opens this page in the system browser.
  if (window.location.pathname === '/auth/native-facebook-signin') {
    return (
      <BrowserRouter>
        <NativeFacebookSignInPage />
      </BrowserRouter>
    );
  }

  if (window.location.pathname === '/reset-password') {
    return (
      <BrowserRouter>
        <ResetPasswordPage />
      </BrowserRouter>
    );
  }

  return (
    <AuthShell>
      <AnalyticsProvider>
        <QueryClientProvider client={queryClient}>
          <RealtimeBridge />
          {/* Boots the Intercom support messenger and syncs its session with auth.
              Inside QueryClientProvider: fetches the Identity-Verification hash via
              a generated React Query hook. Self-disables without an app id / under E2E. */}
          <IntercomMessenger />
          {/* Inside QueryClientProvider: LaunchDarklyProvider reads the player via
              useAppAuth, which issues a React Query request. */}
          <LaunchDarklyProvider>
            <RealtimeManagerProvider>
              <RealtimePrivateBridge>
                <PrivateRealtimeBridge />
                {/* Router wraps the routed pages AND the global overlays below.
                    The overlays are siblings of the routed tree (they float over any
                    route), but several still need router context — SignupStepMethod
                    reads useLocation() for analytics, NotificationDetail uses <Link>
                    to deep-link into a page. So the Router lives here, not inside
                    AppRouter, so that context reaches the whole overlay group. */}
                <BrowserRouter>
                  <AppRouter />
                  <AuthModal />
                  {/* Full-screen email-verification page: self-gates on an in-progress
                      signup with an unverified email; stable mount for the Firebase watcher. */}
                  <EmailVerificationPage />
                  <OtpModal />
                  <TermsGate />
                  <KycGate />
                  <KycActionGate />
                  <TerritoryBlockGate />
                  <KillSwitchGate />
                  <DepositLimitGate />
                  <AccountStatusGate />
                  {/* Global notification pop-up host — opened from the bell/list rows. */}
                  <NotificationDetail />
                  {/* Pop-in banner for a just-arrived notification (pairs with the bell ring). */}
                  <IncomingNotificationBanner />
                  {/* One-shot full-screen FTUE (exp.ftue.experience) for fresh
                      post-signup players. Sits BELOW the compliance gates above
                      (z-30 vs their z-40+) — legal always outranks onboarding. */}
                  <FtueGate />
                  {/* Dev builds only (compile-time gated, tree-shaken from prod):
                      force experiment variants + inspect LD evaluations/exposures. */}
                  <ExperimentsDevtools />
                </BrowserRouter>
              </RealtimePrivateBridge>
            </RealtimeManagerProvider>
          </LaunchDarklyProvider>
        </QueryClientProvider>
      </AnalyticsProvider>
    </AuthShell>
  );
}

export default App;
